import React from 'react';
import { BaseComponentProps } from '../../types';
import { useSettingsTranslation } from '../../modules/i18n';

interface SettingsSidebarProps extends BaseComponentProps {
  isVisible?: boolean;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  hideHeader?: boolean;
  activeTab: 'profile' | 'password';
  onTabChange: (tab: 'profile' | 'password') => void;
}

const SettingsSidebar: React.FC<SettingsSidebarProps> = ({
  className = '',
  isVisible = true,
  isCollapsed = false,
  onToggleCollapse,
  hideHeader = false,
  activeTab,
  onTabChange
}) => {
  const { t } = useSettingsTranslation();

  if (!isVisible) return null;

  const settingsTabs = [
    {
      id: 'profile' as const,
      label: t('tabs.profile.title'),
      icon: (
        <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z'
          />
        </svg>
      ),
      description: t('tabs.profile.description')
    }
    // {
    //     id: 'password' as const,
    //     label: t('tabs.password.title'),
    //     icon: (
    //         <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    //             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    //         </svg>
    //     ),
    //     description: t('tabs.password.description')
    // }
  ];

  return (
    <div
      className={`flex flex-col rounded-lg border border-gray-200 bg-white transition-all duration-300 dark:border-border-dark dark:bg-dark-background-secondary ${
        isCollapsed ? 'w-16' : 'w-80'
      } ${className}`}
    >
      {/* Header */}
      {!hideHeader && (
        <div
          className={`flex items-center border-b border-gray-200 dark:border-border-dark ${isCollapsed ? 'justify-center p-3' : 'justify-between p-4'}`}
        >
          <h2
            className={`font-semibold text-gray-900 transition-opacity duration-300 dark:text-dark-text ${
              isCollapsed ? 'w-0 overflow-hidden opacity-0' : 'opacity-100'
            }`}
          >
            {t('sidebar.title')}
          </h2>
          {onToggleCollapse && (
            <button
              onClick={onToggleCollapse}
              className='rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-dark-text-secondary dark:hover:bg-dark-background-hover dark:hover:text-dark-text'
              title={isCollapsed ? t('sidebar.expandTooltip') : t('sidebar.collapseTooltip')}
            >
              <svg
                className={`h-4 w-4 transition-transform duration-300 ${isCollapsed ? 'rotate-180' : ''}`}
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 19l-7-7 7-7' />
              </svg>
            </button>
          )}
        </div>
      )}

      {/* Navigation */}
      <div className='flex-1 overflow-y-auto scrollbar-thin'>
        <div className='p-2'>
          {settingsTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className={`group flex w-full items-center gap-3 rounded-lg transition-colors duration-200 ${isCollapsed ? 'justify-center p-3' : 'p-3'} ${
                activeTab === tab.id
                  ? 'bg-primary text-white dark:bg-primary'
                  : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-dark-text-secondary dark:hover:bg-dark-background-hover dark:hover:text-dark-text'
              }`}
              style={{ minHeight: '60px' }}
              title={isCollapsed ? tab.label : undefined}
            >
              <div
                className={`flex-shrink-0 ${
                  activeTab === tab.id ? 'text-white' : 'text-gray-500 group-hover:text-gray-700 dark:text-dark-text-secondary dark:group-hover:text-dark-text-secondary'
                }`}
              >
                {tab.icon}
              </div>

              {!isCollapsed && (
                <div className='flex-1 text-left'>
                  <div className={`text-sm font-medium ${activeTab === tab.id ? 'text-white' : ''}`}>{tab.label}</div>
                  <div className={`mt-0.5 text-xs ${activeTab === tab.id ? 'text-white/80' : 'text-gray-500 dark:text-dark-text-secondary'}`}>
                    {tab.description}
                  </div>
                </div>
              )}

              {/* Active indicator */}
              {activeTab === tab.id && !isCollapsed && <div className='h-2 w-2 flex-shrink-0 rounded-full bg-white'></div>}
            </button>
          ))}
        </div>
      </div>

      {/* Footer info */}
      {!isCollapsed && (
        <div className='border-t border-gray-200 p-4 dark:border-border-dark'>
          <div className='text-xs text-gray-500 dark:text-dark-text-secondary'>
            <div className='mb-1 flex items-center gap-2'>
              <svg className='h-3 w-3' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z'
                />
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' />
              </svg>
              <span>{t('sidebar.description')}</span>
            </div>
            <div className='text-gray-400 dark:text-dark-text-secondary'>{t('sidebar.subtitle')}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export { SettingsSidebar };
export default SettingsSidebar;
